import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import HomePage from './pages/HomePage';
import HomepageDraftPage from './pages/HomepageDraftPage';
import CatalogPage from './pages/CatalogPage';
import FAQPage from './pages/FAQPage';
import ProductPageNew from './pages/ProductPageNew';
import ProductPageRoute from './pages/ProductPageRoute';
import GPTCatalogPage from './pages/GPTCatalogPage';
import AboutPage from './pages/AboutPage';
import CbrPage from './pages/CbrPage';
import GenAiCreativePage from './pages/GenAiCreativePage';
import ServicesPage from './pages/ServicesPage';
import EnterpriseSystemsPage from './pages/EnterpriseSystemsPage';
import AiAgentsPlatformPage from './pages/AiAgentsPlatformPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminPanelPage from './pages/AdminPanelPage';
import OrderDetailPage from './pages/OrderDetailPage';
import { LegacyCartRedirect } from './lib/cartNavigation';
import CheckoutPage from './pages/CheckoutPage';
import ScrollToTop from './components/ScrollToTop';
import UtmCapture from './components/UtmCapture';
import YandexMetrikaHitTracker from './components/YandexMetrikaHitTracker';
import CookieConsentBanner from './components/CookieConsentBanner';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiesPolicyPage from './pages/CookiesPolicyPage';
import { initYandexMetrikaIfConsented } from './lib/yandexMetrika';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/toaster';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import NovostiAntologiaPage from './pages/NovostiAntologiaPage';
import NewsPublicationPage from './pages/NewsPublicationPage';
import ReadyForAIPage from './pages/ReadyForAIPage';
import ReadyForAISalesPage from './pages/ReadyForAISalesPage';
import DiscussTaskPage from './pages/DiscussTaskPage';
import ChecklistResultPreviewPage from './pages/ChecklistResultPreviewPage';
import ChecklistSalesEmailPreviewPage from './pages/ChecklistSalesEmailPreviewPage';

/** Старые URL /vendors/:slug → канонический каталог /solutions/:slug */
function LegacyVendorSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={slug ? `/solutions/${slug}` : '/solutions'} replace />;
}

/** Legacy: /catalog/communications → /catalog/ofis_i_kommunikatsii */
function LegacyCommunicationsCatalogRedirect() {
  const { sectionSlug } = useParams();
  const base = '/catalog/ofis_i_kommunikatsii';
  return (
    <Navigate
      to={sectionSlug ? `${base}/${encodeURIComponent(sectionSlug)}` : base}
      replace
    />
  );
}

function App() {
  React.useEffect(() => {
    initYandexMetrikaIfConsented();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <UtmCapture />
          <YandexMetrikaHitTracker />
          <CookieConsentBanner />
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/homepage-draft" element={<HomepageDraftPage />} />
            <Route path="/solutions" element={<CatalogPage />} />
            <Route path="/solutions/:vendorSlug" element={<CatalogPage />} />
            <Route path="/solutions/:vendorSlug/:solutionSlug" element={<ProductPageRoute />} />
            <Route path="/catalog" element={<CatalogPage />} />
            {/* Legacy: communications merged into ofis_i_kommunikatsii */}
            <Route
              path="/catalog/communications"
              element={<LegacyCommunicationsCatalogRedirect />}
            />
            <Route
              path="/catalog/communications/:sectionSlug"
              element={<LegacyCommunicationsCatalogRedirect />}
            />
            <Route path="/catalog/:categoryId" element={<CatalogPage />} />
            <Route path="/catalog/:categoryId/:sectionSlug" element={<CatalogPage />} />
            <Route path="/vendors" element={<Navigate to="/solutions" replace />} />
            <Route path="/vendors/:slug" element={<LegacyVendorSlugRedirect />} />
            <Route path="/cart" element={<LegacyCartRedirect />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiesPolicyPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/novosti-antologia" element={<NovostiAntologiaPage />} />
            <Route path="/novosti-antologia/publikaciya/:slug" element={<NewsPublicationPage />} />
            <Route path="/novosti-antologia/:categorySlug" element={<NovostiAntologiaPage />} />
            <Route path="/ready-for-ai" element={<ReadyForAIPage />} />
            <Route path="/ready-for-ai/result-preview" element={<ChecklistResultPreviewPage />} />
            <Route path="/ready-for-ai-sales/email-preview" element={<ChecklistSalesEmailPreviewPage />} />
            <Route path="/ready-for-ai-sales" element={<ReadyForAISalesPage />} />
            <Route path="/discuss-ai" element={<DiscussTaskPage />} />
            <Route path="/product/:id" element={<ProductPageRoute />} />
            <Route path="/product-new/:id" element={<ProductPageNew />} />
            <Route path="/solution-preview" element={<Navigate to="/product/ai-agent?template=solution" replace />} />
            <Route path="/offer" element={<Navigate to="/404" replace />} />
            <Route path="/landing/crm-deals" element={<Navigate to="/404" replace />} />
            <Route path="/gpt-catalog" element={<GPTCatalogPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/ai-agents-platform" element={<AiAgentsPlatformPage />} />
            <Route path="/enterprise-systems" element={<EnterpriseSystemsPage />} />
            <Route path="/uslugi" element={<Navigate to="/services" replace />} />
            <Route path="/cbr" element={<CbrPage />} />
            <Route path="/gen-ai-creative" element={<GenAiCreativePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route path="/utech/login" element={<AdminLoginPage />} />
            <Route path="/utech" element={<AdminPanelPage />} />
            <Route path="/utech/orders/:orderId" element={<OrderDetailPage />} />
            <Route path="/utech/orders/by-number/:orderNumber" element={<OrderDetailPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
// Test CI/CD deployment - Fri Apr 17 14:50:39 MSK 2026
// CI/CD re-test - Fri Apr 17 15:11:18 MSK 2026
